import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

interface MenuItem {
	icon?: string
    text?: string;
    href : string;
    subitems?: MenuItem[];
  }

@customElement('vxc-navbar')
export class TNavbar extends LitElement {
    static styles = css`
    :host {
      display: block;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 1000;
    }

    .navbar {
      display: flex;
      justify-content: space-between; 
      align-items: center; 
      padding: 1rem;
      background-color: rgba(33, 37, 41, 0.8);
      backdrop-filter: blur(10px);
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: white;
      text-decoration: none;
      font-size: 1.25rem;
      margin-right: auto;
    }

    .nav-container {
      flex: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .nav-menu {
      display: flex;
      gap: 1rem;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .nav-menu-center {
      margin: 0 auto;
    }

    .nav-menu-right {
      margin-left: auto;
    }

    .nav-item {
      position: relative;
    }

    .nav-link {
      color: rgba(255, 255, 255, 0.7);
      text-decoration: none;
      padding: 0.5rem;
      transition: color 0.3s;
    }

    .nav-link:hover,
    .nav-link.active {
      color: white;
    }

    .dropdown {
      position: relative;
    }

    .dropdown-menu {
      display: none;
      position: absolute;
      top: 100%;
      right: -2rem;
      background-color: rgba(33, 37, 41, 0.9);
      padding: 1rem 0 0 0;
      border-radius: 0.25rem;
      min-width: 10rem;
    }

    .dropdown:hover .dropdown-menu {
      display: block;
    }

    .dropdown-item {
      display: block;
      padding: 0.5rem 1rem;
      color: rgba(255, 255, 255, 0.7);
      text-decoration: none;
    }

    .dropdown-item:hover {
      background-color: rgba(255, 255, 255, 0.1);
      color: white;
    }

    .navbar-toggle {
      display: none;
      background: none;
      border: none;
      color: white;
      padding: 0.5rem;
      cursor: pointer;
    }

    @media (max-width: 768px) {
      .navbar-toggle {
        display: block;
      }

      .nav-container {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        flex-direction: column;
        background-color: rgba(33, 37, 41, 0.9);
        padding: 1rem;
      }

      .nav-container.active {
        display: flex;
      }

      .nav-menu {
        flex-direction: column;
        width: 100%;
      }

      .nav-menu-center{
        margin: 0;
        margin-bottom: 1rem;
      }

      .nav-menu-right {
        margin-left: auto;
      }

      .dropdown-menu {
        position: static;
        background-color: transparent;
        padding-left: 1rem;
      }

      .dropdown:hover .dropdown-menu {
        display: none;
      }

      .dropdown.active .dropdown-menu {
        display: block;
      }
    }
  `;

	// properties
	@property({ type: Array , attribute: 'menu-items' }) menuItems: MenuItem[] = [];
	@property({ type: String, attribute: 'brand-name' }) brandName: string = '';
	@property({ type: String, attribute: 'brand-logo' }) brandLogo: string = '';
	@property({ type: Array , attribute: 'right-menu' }) rightMenu: MenuItem[] = [];

	// states
	@state() private isMenuOpen = false;
	@state() private activeItem: string | null = null;

	private toggleMenu() {
		this.isMenuOpen = !this.isMenuOpen;
	}

	private handleItemClick(href: string, event: Event) {
		event.preventDefault();
		this.activeItem = href;
		// Handle navigation here if needed
	}

	renderNav(menuItems: MenuItem[]) {
		console.log('rendering', menuItems)
		if(!menuItems)	return ''
		return html`
			${menuItems.map(
				item => html`
				<li class="nav-item ${item.subitems ? 'dropdown' : ''}">
					<a
					href=${item.href}
					class="nav-link ${this.activeItem === item.href ? 'active' : ''}"
					@click=${(e: Event) => this.handleItemClick(item.href, e)}
					>
					${unsafeHTML(item.icon||'')}
					${item.text}
					</a>
					${item.subitems
					? html`
						<div class="dropdown-menu">
							${item.subitems.map(
							subitem => html`
								<a
								href=${subitem.href}
								class="dropdown-item"
								@click=${(e: Event) =>
									this.handleItemClick(subitem.href, e)}
								>
								${subitem.text}
								</a>
							`
							)}
						</div>
						`
					: ''}
				</li>
			`
		  )}
		`
	}

  render() {
	console.log('render ---->', this.menuItems, this.rightMenu)
    return html`
      <nav class="navbar">
        <a href="/" class="brand">
		  	${unsafeHTML(this.brandLogo)}
          	<span>${this.brandName}</span>
        </a>

        <button
          	class="navbar-toggle"
          	@click=${this.toggleMenu}
          	aria-label="Toggle navigation"
        >
          ☰
        </button>
        <div class="nav-container ${this.isMenuOpen ? 'active' : ''}">
          	<ul class="nav-menu nav-menu-center">
				${this.renderNav(this.menuItems)}
          	</ul>
          	<ul class="nav-menu nav-menu-right">
				${this.renderNav(this.rightMenu)}
          	</ul>
        </div>
      </nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vxc-navbar': TNavbar;
  }
}